import { createSelector } from '@spcc/core'
import type { WeighbridgeOpsState, WeighStage } from '../domain/types'

// Loose state type — domain selectors must work regardless of app store shape
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyState = any

function getSlice(state: AnyState): WeighbridgeOpsState {
  return state.weighbridgeOps as WeighbridgeOpsState
}

export const selectZone = (state: AnyState) => getSlice(state).zone
export const selectZoneFetching = (state: AnyState) => getSlice(state).zoneFetching
export const selectZoneError = (state: AnyState) => getSlice(state).zoneError

export const selectSocketStatus = (state: AnyState) => getSlice(state).socketStatus
export const selectConnectedAt = (state: AnyState) => getSlice(state).connectedAt
export const selectInternetConnected = (state: AnyState) => getSlice(state).internetConnected

export const selectFirstWeightPanel = (state: AnyState) => getSlice(state).firstWeight
export const selectSecondWeightPanel = (state: AnyState) => getSlice(state).secondWeight

export const selectActiveQueue = (state: AnyState) => getSlice(state).activeQueue

export const selectManualOrderNumbers = (state: AnyState) => getSlice(state).manualOrderNumbers
export const selectManualOrderFirst = (state: AnyState) => getSlice(state).manualOrderNumbers.first_weight
export const selectManualOrderSecond = (state: AnyState) => getSlice(state).manualOrderNumbers.second_weight
export const selectManualDirections = (state: AnyState) => getSlice(state).manualDirections
export const selectManualDirectionFirst = (state: AnyState) => getSlice(state).manualDirections.first_weight
export const selectManualDirectionSecond = (state: AnyState) => getSlice(state).manualDirections.second_weight
/** Legacy alias — prefer stage-specific selectors */
export const selectManualOrderNumber = (state: AnyState) => getSlice(state).manualOrderNumbers.first_weight

// ── Stage lock selectors ────────────────────────────────────────────────────
export const selectFirstWeightIsLocked = (state: AnyState) => getSlice(state).firstWeight.isLocked
export const selectFirstWeightLockedByOrder = (state: AnyState) => getSlice(state).firstWeight.lockedByOrder

export const selectSecondWeightIsLocked = (state: AnyState) => getSlice(state).secondWeight.isLocked
export const selectSecondWeightLockedByOrder = (state: AnyState) => getSlice(state).secondWeight.lockedByOrder

export const selectIsLockedByStage = (stage: WeighStage) => (state: AnyState) =>
  stage === 'first_weight' ? getSlice(state).firstWeight.isLocked : getSlice(state).secondWeight.isLocked

// ── Memoized array selectors — only recompute when their specific input ref changes ─────────

export const selectFirstWeightQueue = createSelector(
  selectActiveQueue,
  (queue) => queue.filter((q) => q.stage === 'first_weight'),
)

export const selectSecondWeightQueue = createSelector(
  selectActiveQueue,
  (queue) => queue.filter((q) => q.stage === 'second_weight'),
)

export const selectQueueByStage = (stage: WeighStage) =>
  createSelector(selectActiveQueue, (queue) => queue.filter((q) => q.stage === stage))

// History is stored in separate per-stage arrays in state so that:
// - first_weight events only update firstWeightHistory → only first panel selector recomputes
// - second_weight events only update secondWeightHistory → only second panel selector recomputes
// - global events (connected/disconnected) update both arrays
// This prevents cross-panel re-renders on every WebSocket event.

export const selectFirstWeightHistory = createSelector(
  (state: AnyState) => getSlice(state).firstWeightHistory,
  (history) => [...history].reverse(),
)

export const selectSecondWeightHistory = createSelector(
  (state: AnyState) => getSlice(state).secondWeightHistory,
  (history) => [...history].reverse(),
)

export const selectHistoryByStage = (stage: WeighStage) =>
  stage === 'first_weight' ? selectFirstWeightHistory : selectSecondWeightHistory
